import { useState } from 'react'

export interface ClassificationResult {
  chapter: {
    code: string;
    description: string;
  };
  heading: {
    code: string;
    description: string;
  };
  subheading?: {
    code: string;
    description: string;
  };
  tariffItem?: {
    code: string;
    description: string;
    mfn: string;
  };
  statisticsLevel?: {
    code: string;
    description: string | string[];
    surtax: string;
    agencies: Array<{
      CFIA?: string;
      CNSC?: string;
      DFO?: string;
      ECCC?: string;
      GAC?: string;
      HC?: string;
      PHAC?: string;
      NRCan?: string;
      TC?: string;
    }>;
  };
  sima?: {
    measureInForce: string;
    origins: {
      [country: string]: {
        antiDumping: number | string;
        countervailing: string;
      };
    }[];
  };
}


const AGENCY_NAMES = {
  CFIA: 'Canada Food Inspection Agency',
  CNSC: 'Canadian Nuclear Safety Commission',
  DFO: 'Fisheries and Oceans Canada',
  ECCC: 'Environment and Climate Change Canada',
  GAC: 'Global Affairs Canada',
  HC: 'Health Canada',
  PHAC: 'Public Health Agency of Canada',
  NRCan: 'Natural Resources Canada',
  TC: 'Transport Canada'
};

const formatCode = (code: string) => {
  if (code.length === 2) return code;
  if (code.length === 4) return code;
  if (code.length === 6) return `${code.slice(0, 4)}.${code.slice(4)}`;
  if (code.length === 8) return `${code.slice(0, 4)}.${code.slice(4, 6)}.${code.slice(6)}`;
  if (code.length === 10) return `${code.slice(0, 4)}.${code.slice(4, 6)}.${code.slice(6, 8)}.${code.slice(8)}`;
  return code;
};

const formatAntiDumping = (value: number | string | undefined) => {
  if (typeof value === 'number') {
    return `${(value * 100).toFixed(1)}%`;
  }
  return value;
};

export const HSCodeTree = ({ result }: { result: ClassificationResult }) => {
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  return (
    <div className="mt-6 font-mono">
      <div className="space-y-2">
        {/* Chapter */}
        <div className="text-gray-900 dark:text-gray-100">
          {formatCode(result.chapter.code)} - {result.chapter.description}
        </div>

        {/* Heading */}
        {result.heading && (
          <div className="text-gray-900 dark:text-gray-100">
            |{"\u00A0"}{formatCode(result.heading.code)} - {result.heading.description}
          </div>
        )}

        {/* Subheading */}
        {result.subheading && (
          <div className="text-gray-900 dark:text-gray-100">
            |{"\u00A0\u00A0"}{formatCode(result.subheading.code)} - {result.subheading.description}
          </div>
        )}

        {/* Tariff Item */}
        {result.tariffItem && (
          <div className="text-gray-900 dark:text-gray-100">
            |{"\u00A0\u00A0\u00A0"}{formatCode(result.tariffItem.code)} - {result.tariffItem.description}
          </div>
        )}

        {/* Statistics Level */}
        {result.statisticsLevel && (
          <div className="text-gray-900 dark:text-gray-100">
            |{"\u00A0\u00A0\u00A0\u00A0"}{formatCode(result.statisticsLevel.code)} - {result.statisticsLevel.description}
          </div>
        )}
      </div>

      {/* Additional Information Section */}
      {(result.statisticsLevel || result.tariffItem) && (
        <div className="mt-6 space-y-4 border-t pt-4 dark:border-gray-700">
          {result.tariffItem && (
            <div className="text-gray-900 dark:text-gray-100">
              <span className="font-semibold">MFN Duty Rate:</span> {result.tariffItem.mfn}
            </div>
          )}

          {result.statisticsLevel?.surtax !== "No" && (
            <div className="text-gray-900 dark:text-gray-100">
              <span className="font-semibold">Surtax:</span> {result.statisticsLevel!.surtax}
            </div>
          )}

          {result.statisticsLevel?.agencies && (
            <div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">
                HS Code is regulated by the following Government agencies:
              </p>
              {(() => {
                const regulatedAgencies = result.statisticsLevel.agencies.filter(
                  agency => Object.values(agency)[0] === "Yes"
                );
                if (regulatedAgencies.length === 0) {
                  return <p className="mt-2 text-gray-700 dark:text-gray-300">N/A</p>;
                }
                return (
                  <ul className="mt-2 list-disc list-inside text-gray-700 dark:text-gray-300">
                    {regulatedAgencies.map((agency, index) => {
                      const acronym = Object.keys(agency)[0];
                      return (
                        <li key={index}>
                          {acronym} ({AGENCY_NAMES[acronym as keyof typeof AGENCY_NAMES]})
                        </li>
                      );
                    })}
                  </ul>
                );
              })()}
            </div>
          )}

          {result.sima && (
            <div className="text-gray-900 dark:text-gray-100">
              <p className="font-semibold mb-2">Special Import Measures Act (SIMA)</p>
              <p className="mb-4">{result.sima.measureInForce}</p>
              
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="w-64 rounded-md border-2 border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-indigo-500 dark:focus:ring-indigo-400 px-4 py-2 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
              >
                <option value="">Select origin country</option>
                {result.sima.origins.map((origin, index) => {
                  const country = Object.keys(origin)[0];
                  return (
                    <option key={index} value={country}>
                      {country}
                    </option>
                  );
                })}
              </select>

              {selectedCountry && result.sima.origins.find(origin => origin[selectedCountry]) && (
                <div className="mt-4 space-y-2">
                  <p>
                    <span className="font-medium">Anti-Dumping:</span>{" "}
                    {formatAntiDumping(result.sima.origins.find(origin => origin[selectedCountry])?.[selectedCountry].antiDumping)}
                  </p>
                  <p>
                    <span className="font-medium">Countervailing:</span>{" "}
                    {result.sima.origins.find(origin => origin[selectedCountry])?.[selectedCountry].countervailing}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  );
};




